export interface UserDocumentDto {
  UserDocument_id: number;
  UserDocument_guid: string;
  UserDocument_Name: string;
  UserDocument_CreatedAt: string;
  UserDocument_UpdatedAt: string;
  // The case this document is filed under, or null for client-level (present on manager list responses).
  Ticket_guid?: string | null;
  // Whether the client can see this document in their portal (present on manager responses).
  UserDocument_ClientVisible?: boolean;
}

export interface DocumentsResponse {
  documents: UserDocumentDto[];
  count: number;
}

export interface UploadResponse {
  message: string;
  user: {
    guid: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  document: UserDocumentDto;
  ocr?: {
    dates: string[];
    times: string[];
    trialType?: 'pretrial' | 'trial';
  };
  // Vision/LLM calendar extraction (present when uploaded against a Ticket_guid). The dashboard
  // pre-fills the court-event form from `events[0]`; `confidence` drives the "please verify" hint.
  calendarExtraction?: {
    processed: boolean;
    reason?: string;
    events?: Array<{
      title: string;
      date: string; // ISO YYYY-MM-DD
      time?: string; // 24h HH:MM
      court?: string;
      description?: string;
    }>;
    confidence?: 'high' | 'medium' | 'low';
  };
}

/** Result of a batch upload — the files that landed, and the ones that did not, named individually. */
export interface BatchUploadResponse {
  message: string;
  documents: UserDocumentDto[];
  failed: Array<{ name: string; error: string }>;
  uploadedCount: number;
  failedCount: number;
}

/** Files staff can attach — mirrors the server's allow-list. */
export const ACCEPTED_DOCUMENT_TYPES =
  '.pdf,.jpg,.jpeg,.png,.doc,.docx,application/pdf,image/jpeg,image/png,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document';

/** Matches the server's per-request cap. */
export const MAX_BATCH_FILES = 20;

class UserDocumentApi {
  // Get documents for a specific user (manager only). Pass ticketGuid to scope the list to a
  // single case/citation; omit it to get every document for the client.
  static async getUserDocuments(userGuid: string, ticketGuid?: string): Promise<UserDocumentDto[]> {
    const apiHost = process.env.API_HOST?.trim() || window.location.origin;

    const query = ticketGuid ? `?Ticket_guid=${encodeURIComponent(ticketGuid)}` : '';
    const response = await fetch(
      `${apiHost}/api/v1/documents/manager/user/${userGuid}${query}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      }
    );

    if (response.ok) {
      const data: DocumentsResponse = await response.json();
      return data.documents || [];
    } else {
      const errorData = await response.json().catch(() => ({ error: 'Failed to fetch documents' }));
      throw new Error(errorData.error || `Failed to fetch documents: ${response.status} ${response.statusText}`);
    }
  }

  // Upload a document for a specific user (manager only).
  // - ticketGuid files the document under a specific case/citation (omit for client-level).
  // - extractCalendar additionally runs the vision/LLM court-date extraction (court-event flow only);
  //   plain document attachments leave it off so no LLM call is made.
  static async uploadDocument(
    userGuid: string,
    file: File,
    includeOcr: boolean = false,
    ticketGuid?: string,
    extractCalendar: boolean = false,
  ): Promise<UploadResponse> {
    const apiHost = process.env.API_HOST?.trim() || window.location.origin;

    const formData = new FormData();
    formData.append('document', file);

    // Build query: ?ocr (legacy date heuristics), ?Ticket_guid (file under case),
    // ?extractCalendar (vision/LLM calendar extraction — court-event flow only).
    const params = new URLSearchParams();
    if (includeOcr) params.append('ocr', 'true');
    if (ticketGuid) params.append('Ticket_guid', ticketGuid);
    if (extractCalendar) params.append('extractCalendar', 'true');
    const query = params.toString() ? `?${params.toString()}` : '';

    const response = await fetch(
      `${apiHost}/api/v1/documents/manager/user/${userGuid}/upload${query}`,
      {
        method: 'POST',
        credentials: 'include',
        body: formData,
      }
    );

    if (response.ok) {
      const data: UploadResponse = await response.json();
      return data;
    } else {
      const errorData = await response.json().catch(() => ({ error: 'Failed to upload document' }));
      throw new Error(errorData.error || 'Failed to upload document');
    }
  }

  /**
   * Upload several documents at once for a client (manager only).
   *
   * A client typically sends citation + licence + insurance + court notices together, so staff pick
   * them all in one go. Files are reported individually: an unsupported or failed file comes back in
   * `failed` while the rest still upload, so nothing is silently dropped.
   *
   * Pass ticketGuid to file the whole batch under one case/citation.
   */
  static async uploadDocuments(
    userGuid: string,
    files: File[],
    ticketGuid?: string,
  ): Promise<BatchUploadResponse> {
    const apiHost = process.env.API_HOST?.trim() || window.location.origin;

    const formData = new FormData();
    files.forEach((file) => formData.append('documents', file));

    const query = ticketGuid ? `?Ticket_guid=${encodeURIComponent(ticketGuid)}` : '';

    const response = await fetch(
      `${apiHost}/api/v1/documents/manager/user/${userGuid}/upload-multiple${query}`,
      {
        method: 'POST',
        credentials: 'include',
        body: formData,
      },
    );

    const data = await response.json().catch(() => null);

    // The server answers 400 when nothing landed — that body still names every failure, so surface
    // it as a result rather than a bare error whenever it is shaped like one.
    if (!response.ok) {
      if (data && Array.isArray(data.failed)) {
        return data as BatchUploadResponse;
      }
      throw new Error(data?.error || data?.message || 'Failed to upload documents');
    }

    return data as BatchUploadResponse;
  }

  // Delete a specific document for a specific user (manager only)
  static async deleteDocument(userGuid: string, documentGuid: string): Promise<void> {
    const apiHost = process.env.API_HOST?.trim() || window.location.origin;
    
    const response = await fetch(
      `${apiHost}/api/v1/documents/manager/user/${userGuid}/${documentGuid}`,
      {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Failed to delete document' }));
      throw new Error(errorData.error || errorData.message || 'Failed to delete document');
    }
  }

  // Set whether the client can see a document in their portal (manager only)
  static async setDocumentVisibility(
    userGuid: string,
    documentGuid: string,
    clientVisible: boolean,
  ): Promise<UserDocumentDto> {
    const apiHost = process.env.API_HOST?.trim() || window.location.origin;

    const response = await fetch(
      `${apiHost}/api/v1/documents/manager/user/${userGuid}/${documentGuid}/visibility`,
      {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ clientVisible }),
      }
    );

    if (response.ok) {
      return await response.json();
    } else {
      const errorData = await response
        .json()
        .catch(() => ({ error: 'Failed to update visibility' }));
      throw new Error(errorData.error || 'Failed to update document visibility');
    }
  }

  // Document URL for manager fetch/open (relative when API_HOST unset — same as client documents).
  static getManagerDocumentUrl(userGuid: string, documentGuid: string): string {
    return `${process.env.API_HOST || ''}/api/v1/documents/manager/user/${userGuid}/${documentGuid}`;
  }

  // Get download URL for a specific document
  static getDocumentDownloadUrl(userGuid: string, documentGuid: string): string {
    return UserDocumentApi.getManagerDocumentUrl(userGuid, documentGuid);
  }

  // Helper function to check if file is an image
  static isImageFile(fileName: string): boolean {
    const imageExtensions = ['.png', '.jpg', '.jpeg'];
    const extension = fileName.toLowerCase().substring(fileName.lastIndexOf('.'));
    return imageExtensions.includes(extension);
  }

  // View in a new tab — fetch with session cookie, then open a blob URL (works across dev proxy / API_HOST).
  static async viewDocument(
    userGuid: string,
    userDocument: UserDocumentDto,
  ): Promise<void> {
    const fileUrl = UserDocumentApi.getManagerDocumentUrl(
      userGuid,
      userDocument.UserDocument_guid,
    );

    const response = await fetch(fileUrl, { credentials: 'include' });
    if (!response.ok) {
      throw new Error('Failed to open document');
    }

    const buffer = await response.arrayBuffer();
    const name = userDocument.UserDocument_Name.toLowerCase();
    let contentType = response.headers.get('Content-Type') ?? 'application/octet-stream';

    if (UserDocumentApi.isImageFile(userDocument.UserDocument_Name)) {
      if (name.endsWith('.png')) {
        contentType = 'image/png';
      } else if (name.endsWith('.jpg') || name.endsWith('.jpeg')) {
        contentType = 'image/jpeg';
      }
    } else if (name.endsWith('.pdf')) {
      contentType = 'application/pdf';
    }

    const blob = new Blob([buffer], { type: contentType });
    const blobUrl = URL.createObjectURL(blob);
    window.open(blobUrl, '_blank');
  }

  // Force download (manager menu) — mirrors client Settings → Documents download.
  static async downloadDocument(
    userGuid: string,
    userDocument: UserDocumentDto,
  ): Promise<void> {
    const fileUrl = UserDocumentApi.getManagerDocumentUrl(
      userGuid,
      userDocument.UserDocument_guid,
    );

    if (UserDocumentApi.isImageFile(userDocument.UserDocument_Name)) {
      const response = await fetch(fileUrl, { credentials: 'include' });
      if (!response.ok) {
        throw new Error('Failed to download file');
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = userDocument.UserDocument_Name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      return;
    }

    const link = document.createElement('a');
    link.href = fileUrl;
    link.download = userDocument.UserDocument_Name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  // Helper function to handle file download/view
  static handleFileClick(userGuid: string, userDocument: UserDocumentDto): void {
    const fileUrl = UserDocumentApi.getManagerDocumentUrl(
      userGuid,
      userDocument.UserDocument_guid,
    );

    if (UserDocumentApi.isImageFile(userDocument.UserDocument_Name)) {
      window.open(fileUrl, '_blank');
    } else {
      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = userDocument.UserDocument_Name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }
}

export default UserDocumentApi;